import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { SrNotificationItem } from '../../services/apiService';
import { useSrNotifications } from '../../hooks/useSrNotifications';

interface SrNotificationsBellProps {
  enabled: boolean;
}

function NotificationSection({
  title,
  items,
  accent,
  onSelect,
}: {
  title: string;
  items: SrNotificationItem[];
  accent?: 'danger';
  onSelect: (item: SrNotificationItem) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div style={{ padding: '8px 0' }}>
      <div
        style={{
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: accent === 'danger' ? '#c62828' : 'var(--color-text-muted, #6b7280)',
          padding: '4px 12px 8px',
        }}
      >
        {title}
      </div>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item)}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            border: 'none',
            background: 'transparent',
            padding: '10px 12px',
            cursor: 'pointer',
            borderLeft: accent === 'danger' ? '3px solid #c62828' : '3px solid transparent',
            color: 'var(--axis-dark-charcoal, #333E48)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{item.label}</div>
          <div style={{ fontSize: '0.85rem', marginTop: 2 }}>{item.patient_name}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted, #6b7280)', marginTop: 2 }}>
            {item.sr_referral_number}
            {item.event_at ? ` · ${new Date(item.event_at).toLocaleString('en-NZ')}` : ''}
          </div>
        </button>
      ))}
    </div>
  );
}

export const SrNotificationsBell: React.FC<SrNotificationsBellProps> = ({ enabled }) => {
  const { feed } = useSrNotifications(enabled);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!enabled) return null;

  const actionCount = feed?.action_required_count ?? 0;
  const actionItems = feed?.action_required ?? [];
  const otherItems = feed?.other ?? [];
  const isEmpty = actionItems.length === 0 && otherItems.length === 0;

  const handleSelect = (item: SrNotificationItem) => {
    setOpen(false);
    navigate(`/queue?referralId=${item.referral_id}`);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', marginRight: 12 }}>
      <button
        type="button"
        aria-label="SR updates"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          position: 'relative',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 6,
          color: 'inherit',
        }}
      >
        <FeatherIcon icon="bell" size={18} />
        {actionCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              background: '#c62828',
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}
          >
            {actionCount}
          </span>
        )}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            width: 320,
            maxHeight: 420,
            overflowY: 'auto',
            background: '#fff',
            color: 'var(--axis-dark-charcoal, #333E48)',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 1000,
          }}
        >
          <div style={{ padding: '12px 12px 4px', fontWeight: 600 }}>SR Updates</div>
          {isEmpty ? (
            <div style={{ padding: '16px 12px', color: 'var(--color-text-muted, #6b7280)' }}>
              No recent SR updates
            </div>
          ) : (
            <>
              <NotificationSection
                title="Action required"
                items={actionItems}
                accent="danger"
                onSelect={handleSelect}
              />
              {actionItems.length > 0 && otherItems.length > 0 && (
                <div style={{ borderTop: '1px solid var(--color-border, #e5e7eb)' }} />
              )}
              <NotificationSection title="Other" items={otherItems} onSelect={handleSelect} />
            </>
          )}
        </div>
      )}
    </div>
  );
};
